import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  ListOrdered,
  FileQuestion,
  FlaskConical,
  Plus,
  MessageCircle,
  LogIn,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { useLanguage } from "@/context/LanguageContext";
import { nativeAppLabels } from "@/i18n/labels/commonLabels";
import { hapticImpact } from "@/lib/native/capacitorBridge";
import {
  shouldHideBottomNav,
  shouldShowGuestBottomNav,
  normalizePath,
} from "@/lib/native/nativeRoutes";
import { CreateTestDialog } from "@/components/car-test/CreateTestDialog";
import { useNativeWebChatPrompt } from "@/hooks/useNativeWebChatPrompt";
import { cn } from "@/lib/utils";
import { closeIntercomMessenger } from "@/lib/intercom/intercomMessenger";

type NavItem = {
  key: string;
  to?: string;
  icon: typeof Home;
  labelKey: keyof ReturnType<typeof nativeAppLabels>;
  onClick?: () => void;
};

const ADMIN_ITEMS: NavItem[] = [
  { key: "auctions", to: "/auctions", icon: ListOrdered, labelKey: "auctions" },
  {
    key: "leadbox",
    to: "/admin/leadbox",
    icon: FileQuestion,
    labelKey: "leadbox",
  },
  {
    key: "tests",
    to: "/admin/car-test",
    icon: FlaskConical,
    labelKey: "tests",
  },
  { key: "home", to: "/dashboard", icon: Home, labelKey: "home" },
];

const DEALER_ITEMS: NavItem[] = [
  { key: "auctions", to: "/auctions", icon: ListOrdered, labelKey: "auctions" },
  { key: "tests", to: "/car-test", icon: FlaskConical, labelKey: "tests" },
  {
    key: "messages",
    icon: MessageCircle,
    labelKey: "chat",
  },
  { key: "home", to: "/dashboard", icon: Home, labelKey: "home" },
];

const GUEST_ITEMS: NavItem[] = [
  { key: "auctions", to: "/auctions", icon: ListOrdered, labelKey: "auctions" },
  { key: "login", to: "/auth", icon: LogIn, labelKey: "login" },
];

type IndicatorState = {
  left: number;
  width: number;
  opacity: number;
};

type NavBarProps = {
  items: NavItem[];
  ariaLabel: string;
  guestMode?: boolean;
  onMessages?: () => void;
  centerFab?: React.ReactNode;
};

const NativeNavBar = ({
  items,
  ariaLabel,
  guestMode = false,
  onMessages,
  centerFab,
}: NavBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const labels = nativeAppLabels(language);
  const pillRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<IndicatorState>({
    left: 0,
    width: 0,
    opacity: 0,
  });

  const path = normalizePath(location.pathname);

  const isActive = useCallback(
    (to: string) => {
      if (to === "/auctions") {
        return (
          path === "/auctions" ||
          path === "/auctions/all" ||
          /^\/auctions\/[^/]+$/.test(path)
        );
      }
      return path === to || path.startsWith(`${to}/`);
    },
    [path],
  );

  const updateIndicator = useCallback(() => {
    const activeIndex = items.findIndex((item) => item.to && isActive(item.to));
    const pill = pillRef.current;

    if (activeIndex < 0 || !pill) {
      setIndicator((prev) =>
        prev.opacity === 0 ? prev : { ...prev, opacity: 0 },
      );
      return;
    }

    const pillRect = pill.getBoundingClientRect();

    if (guestMode) {
      const count = items.length;
      const slotWidth = pillRect.width / count;
      const inset = 8;
      const width = Math.max(0, slotWidth - inset * 2);
      const left = activeIndex * slotWidth + inset;

      setIndicator((prev) => {
        if (prev.left === left && prev.width === width && prev.opacity === 1) {
          return prev;
        }
        return { left, width, opacity: 1 };
      });
      return;
    }

    const activeItem = items[activeIndex];
    const btn = buttonRefs.current[activeItem.key];
    if (!btn) return;

    const btnRect = btn.getBoundingClientRect();
    const left = btnRect.left - pillRect.left;
    const width = btnRect.width;

    setIndicator((prev) => {
      if (prev.left === left && prev.width === width && prev.opacity === 1) {
        return prev;
      }
      return { left, width, opacity: 1 };
    });
  }, [items, isActive, guestMode]);

  useLayoutEffect(() => {
    updateIndicator();
    const raf = requestAnimationFrame(() => updateIndicator());
    window.addEventListener("resize", updateIndicator);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateIndicator);
    };
  }, [updateIndicator, path]);

  const renderNavItem = (item: NavItem) => {
    const label = labels[item.labelKey] ?? String(item.labelKey);
    const active = item.to ? isActive(item.to) : false;
    const Icon = item.icon;

    const handlePress = () => {
      void hapticImpact("light");
      if (item.onClick) {
        item.onClick();
        return;
      }
      if (item.key === "messages") {
        onMessages?.();
        return;
      }
      closeIntercomMessenger();
      if (item.to) navigate(item.to, { replace: true });
    };

    return (
      <button
        key={item.key}
        ref={(el) => {
          buttonRefs.current[item.key] = el;
        }}
        type="button"
        className={cn(
          "native-bottom-nav-item relative z-[1] flex flex-col items-center justify-center gap-0.5 min-h-[44px] flex-1 min-w-0 px-1 py-1.5 rounded-full select-none touch-manipulation",
          "transition-[color,transform] duration-200 ease-out",
          "[-webkit-touch-callout:none] [-webkit-user-select:none]",
          active
            ? "text-foreground font-semibold"
            : "text-foreground/65 hover:text-foreground",
        )}
        onClick={handlePress}
        onContextMenu={(e) => e.preventDefault()}
        aria-label={label}
        aria-current={active ? "page" : undefined}
      >
        <Icon
          className={cn(
            "h-5 w-5 shrink-0 transition-transform duration-200",
            active && "scale-105",
          )}
          strokeWidth={active ? 2.5 : 2}
        />
        <span className="text-[10px] font-medium leading-tight text-center max-w-[4.25rem] truncate">
          {label}
        </span>
      </button>
    );
  };

  const leftItems = centerFab ? items.slice(0, 2) : items;
  const rightItems = centerFab ? items.slice(2) : [];

  return (
    <nav
      className="native-bottom-nav fixed bottom-0 left-0 right-0 z-[80] pointer-events-none"
      aria-label={ariaLabel}
      data-guest={guestMode ? "true" : undefined}
    >
      <div
        ref={pillRef}
        className={cn(
          "native-bottom-nav-pill pointer-events-auto relative mx-3 mb-0 flex items-end gap-0.5 rounded-full border border-border/60 bg-background/75 px-1 pb-1 pt-2 shadow-[0_4px_20px_rgba(0,0,0,0.16)] backdrop-blur-xl select-none overflow-visible [-webkit-touch-callout:none]",
          guestMode && "native-bottom-nav-pill-guest px-2",
        )}
      >
        <div
          className="native-bottom-nav-indicator absolute rounded-full bg-muted pointer-events-none"
          style={{
            left: indicator.left,
            width: indicator.width,
            opacity: indicator.opacity,
            top: 4,
            bottom: 4,
            transition: guestMode
              ? "left 0.28s cubic-bezier(0.32, 0.72, 0, 1), width 0.2s ease, opacity 0.15s ease"
              : undefined,
          }}
          aria-hidden
        />
        {leftItems.map(renderNavItem)}
        {centerFab}
        {rightItems.map(renderNavItem)}
      </div>
    </nav>
  );
};

const NativeBottomNav = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { language } = useLanguage();
  const labels = nativeAppLabels(language);
  const [createTestOpen, setCreateTestOpen] = useState(false);
  const { requestChat } = useNativeWebChatPrompt();

  const path = normalizePath(location.pathname);

  if (shouldHideBottomNav(path)) return null;

  if (!user) {
    if (!shouldShowGuestBottomNav(path)) return null;

    return (
      <NativeNavBar
        items={GUEST_ITEMS}
        ariaLabel={labels.guestTabBar ?? labels.tabBar ?? "Navigation"}
        guestMode
      />
    );
  }

  const isAdmin = user.type === UserType.Admin;
  const items = isAdmin ? ADMIN_ITEMS : DEALER_ITEMS;
  const newTestLabel = labels.newTest ?? "Nytt test";

  const openNewTest = () => {
    void hapticImpact("medium");
    setCreateTestOpen(true);
  };

  const centerFab = (
    <div className="relative z-[1] flex flex-col items-center justify-end flex-1 min-w-0 -mt-3 pb-0.5">
      <button
        type="button"
        className="native-fab-new-test flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-background active:scale-95 transition-transform"
        onClick={openNewTest}
        aria-label={newTestLabel}
      >
        <Plus className="h-7 w-7" strokeWidth={2.5} />
      </button>
      <span className="text-[9px] font-semibold text-primary mt-0.5 leading-tight text-center max-w-[4.5rem] truncate">
        {newTestLabel}
      </span>
    </div>
  );

  return (
    <>
      <NativeNavBar
        items={items}
        ariaLabel={labels.tabBar ?? "Navigation"}
        onMessages={requestChat}
        centerFab={centerFab}
      />

      <CreateTestDialog open={createTestOpen} onOpenChange={setCreateTestOpen} />
    </>
  );
};

export default NativeBottomNav;
